<template>
  <div class="filter-duration-content">
    <UiChips
      :options="visibleChipsList"
      :modelValue="activeChip"
      small
      @update:modelValue="setActiveChip"
    />
    <div class="filter-duration-content__input-group">
      <UiInput
        :value="rawValue[0]"
        :placeholder="t('duration.fromDay', { day: 1 })"
        :maxLength="2"
        @inputValue="(value: string | number) => updateRawValue(value as string, 0)"
        @blur="processRawValue"
      />
      <UiInput
        :value="rawValue[1]"
        :placeholder="t('duration.toDay', { day: categoryStore.maxDuration })"
        :maxLength="2"
        @inputValue="(value: string | number) => updateRawValue(value as string, 1)"
        @blur="processRawValue"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { UiInput } from '@experience/core-input-ui';
  import { UiChips } from '@experience/core-chips-ui';
  import ru from '../i18n/ru.json';
  import { useCategoryStore } from '@/shared/store';
  import { useI18n } from '@experience/i18n';
  import { EExperienceFilterType } from '@/widgets/filtersBlock';
  import { ref, watch, computed } from 'vue';

  type DurationChipOption = {
    label: string;
    value: number;
  };

  type DurationChipValue = number;

  const isDurationChipValue = (value: unknown): value is DurationChipValue => typeof value === 'number';
  const isDurationChipValueArray = (value: unknown): value is DurationChipValue[] =>
    Array.isArray(value) && value.every((item) => isDurationChipValue(item));

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const categoryStore = useCategoryStore();

  const chipsList = ref<DurationChipOption[]>([
    { label: t('duration.untilTwoDay'), value: 2 },
    { label: t('duration.toDay', { day: 4 }), value: 4 },
    { label: t('duration.toDay', { day: 7 }), value: 7 },
    { label: '10+', value: 10 },
  ]);

  const activeChip = ref<DurationChipValue | null>(null);

  // Удаляем лишние чипсы в соответсвии с макимальном количеством дней в туре
  const visibleChipsList = computed(() => {
    return chipsList.value.filter((chip) => {
      return typeof chip.value === 'number' && chip.value <= categoryStore.maxDuration;
    });
  });

  const rawValue = ref([
    categoryStore.tmpFilters[EExperienceFilterType.DURATION].duration[0],
    categoryStore.tmpFilters[EExperienceFilterType.DURATION].duration[1],
  ]);

  const isUpdatingByChips = ref(false);
  const setActiveChip = async (value: unknown) => {
    isUpdatingByChips.value = true;

    const selectedValue = isDurationChipValueArray(value) ? value[0] : isDurationChipValue(value) ? value : null;

    if (!selectedValue) {
      activeChip.value = null;
      await updateActiveChip();
      await updateStore();

      isUpdatingByChips.value = false;

      return;
    }

    const chip = visibleChipsList.value.find((item) => item.value === selectedValue);

    if (!chip) {
      isUpdatingByChips.value = false;

      return;
    }

    activeChip.value = chip.value;

    if (chip.label === '10+') {
      // "10+" → ставим "От", сбрасываем "До"
      rawValue.value = [Number(chip.value), 0];
    } else {
      // обычные чипы → сначала проверим конфликт с "От"
      const firstValue = rawValue.value[0];

      if (firstValue !== 0 && firstValue > Number(chip.value)) {
        // Конфликт — сбрасываем "От"
        rawValue.value = [0, Number(chip.value)];
      } else {
        // Конфликта нет — оставляем "От", ставим "До"
        rawValue.value = [firstValue, Number(chip.value)];
      }
    }

    // await updateActiveChip();
    await updateStore();

    isUpdatingByChips.value = false;
  };

  // Валидируем значения в input, на больше 0 и меньше максимального кол-ва дней
  const MAX_LIMIT = 50;

  const validateInputValue = (value: string) => {
    const normalizeValue = Number(value);

    if (normalizeValue > MAX_LIMIT) {
      return MAX_LIMIT;
    }

    return normalizeValue > 0 ? normalizeValue : 0;
  };

  // После изменения input делаем чипс активным (если значение input соответствует какому-либо чипсу)
  const updateActiveChip = async () => {
    const chip = visibleChipsList.value.find((el) => {
      if (el.label === '10+') {
        return rawValue.value[0] === el.value;
      }

      return rawValue.value[1] === el.value;
    });

    if (chip) {
      activeChip.value = chip.value;
    } else {
      activeChip.value = null;
    }
  };

  const lastChangedIndex = ref<number | null>(null);
  const debounceTimer = ref<number | null>(null);

  const updateRawValue = (value: string, id: number) => {
    if (isUpdatingByChips.value) {
      return;
    }

    rawValue.value[id] = validateInputValue(value);
    lastChangedIndex.value = id;

    // Сбросим предыдущий таймер
    if (debounceTimer.value) {
      clearTimeout(debounceTimer.value);
    }

    // Запустим новый таймер (300 мс)
    debounceTimer.value = window.setTimeout(() => {
      processRawValue();
    }, 600);

    updateActiveChip();
    updateStore();
  };

  function processRawValue() {
    const firstValue = rawValue.value[0];
    const secondValue = rawValue.value[1];

    if (firstValue !== 0 && secondValue !== 0 && firstValue > secondValue) {
      // Меняем местами
      rawValue.value = [secondValue, firstValue];
    }

    updateActiveChip();
    updateStore();
  }

  const updateStore = async () => {
    if (
      categoryStore.tmpFilters[EExperienceFilterType.DURATION].duration[0] !== rawValue.value[0] ||
      categoryStore.tmpFilters[EExperienceFilterType.DURATION].duration[1] !== rawValue.value[1]
    ) {
      await categoryStore.setDuration(rawValue.value[0] ?? 0, rawValue.value[1] ?? 0);
    }
  };

  watch(
    () => categoryStore.tmpFilters[EExperienceFilterType.DURATION].duration,
    () => {
      if (
        categoryStore.tmpFilters[EExperienceFilterType.DURATION].duration[0] !== rawValue.value[0] ||
        categoryStore.tmpFilters[EExperienceFilterType.DURATION].duration[1] !== rawValue.value[1]
      ) {
        isUpdatingByChips.value = true;
        rawValue.value = [...categoryStore.tmpFilters[EExperienceFilterType.DURATION].duration];

        /**
         * чтобы не дергался обработчик inputValue и не вызывал бесконечный цикл обновлений
         */
        window.setTimeout(() => {
          isUpdatingByChips.value = false;
        }, 1000);

        updateActiveChip();
      }
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  .filter-duration-content__input-group {
    display: flex;
    margin-top: 16px;

    :deep(.input__wrap) {
      z-index: 1;

      &:first-child {
        --io-border-radius-top-right: 0;
        --io-border-radius-bottom-right: 0;
        margin-right: -1px;
      }

      &:last-child {
        --io-border-radius-top-left: 0;
        --io-border-radius-bottom-left: 0;
      }

      &:focus-within {
        z-index: 3;
      }
    }
  }
</style>
